/**
 * The software keyboard never covers the field being typed into
 * (ipad-native-shell 13.6, device row 4.10).
 *
 * The shell runs `Keyboard.resize: "body"`: the plugin shrinks `<body>` and
 * nothing else. Measured on the iPad Simulator (2026-10-19): `innerHeight`,
 * `100vh` and the visual viewport all stay at the full screen, so a screen laid
 * out at `min-height: 100vh` and centred (login, signup) keeps its card behind
 * the keyboard — in landscape the password field and "Log in" were covered —
 * and the document has nothing to scroll, so neither WebKit nor
 * `scrollIntoView` can bring the field up. A `position: fixed` overlay (the
 * parent gate, Settings) does not move with a scroll at all.
 *
 * So the keyboard's height is published as `--keyboard-h` and two things use it:
 *   - `global.css` lets the document scroll by that much (a taller `html`), and
 *     this module scrolls the focused field — with its form's submit button
 *     when both fit — into the part of the screen the keyboard leaves;
 *   - `.wbm-safe-overlay` and the Parental_Gate pad their bottom by it, so a
 *     fixed overlay centres in what is left instead.
 *
 * Native only: on the web `--keyboard-h` is never set, and every rule that
 * reads it falls back to 0.
 */
import { onKeyboardHeight } from "../platform/keyboard";

/** Breathing room between the field and the keyboard's top edge. */
const MARGIN = 16;

function isFixed(el: Element | null): boolean {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (getComputedStyle(n).position === "fixed") return true;
  }
  return false;
}

/** The status-bar inset in px (`--safe-top`), resolved through a probe element. */
function safeTop(): number {
  const probe = document.createElement("div");
  probe.style.cssText = "position:absolute;visibility:hidden;padding-top:var(--safe-top, 0px)";
  document.body.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return px;
}

/**
 * Scroll `el` (and its form's last button, when both fit) above the keyboard.
 *
 * When they cannot both fit — a phone in landscape leaves ~190pt above the
 * keyboard (iPhone 17 Pro Simulator, 2026-10-21) — the field goes to the TOP of
 * the visible part, not just above the keyboard: what follows it in the form
 * (the next field, the button) then shows beneath it instead of staying
 * hidden behind the keyboard where it cannot be tapped.
 */
export function revealAboveKeyboard(el: Element, keyboardHeight: number): void {
  if (keyboardHeight <= 0 || isFixed(el)) return;
  const visibleBottom = window.innerHeight - keyboardHeight - MARGIN;
  const visibleTop = safeTop() + MARGIN;
  const field = el.getBoundingClientRect();
  const form = el.closest("form");
  const buttons = form ? form.querySelectorAll("button") : null;
  const submit = form?.querySelector('button[type="submit"]') ?? (buttons?.length ? buttons[buttons.length - 1] : null);
  const want = submit ? Math.max(field.bottom, submit.getBoundingClientRect().bottom) : field.bottom;
  if (want - field.top > visibleBottom - visibleTop) {
    if (field.top !== visibleTop) window.scrollBy({ top: field.top - visibleTop });
    return;
  }
  if (want > visibleBottom) window.scrollBy({ top: want - visibleBottom });
  else if (field.top < visibleTop) window.scrollBy({ top: field.top - visibleTop });
}

let installed = false;

export async function installKeyboardInset(): Promise<void> {
  if (installed) return;
  installed = true;
  const root = document.documentElement;
  let height = 0;
  const reveal = () => {
    const el = document.activeElement;
    if (el && el !== document.body) revealAboveKeyboard(el, height);
  };
  await onKeyboardHeight((px) => {
    height = px;
    if (px > 0) {
      root.style.setProperty("--keyboard-h", `${px}px`);
      // After the taller <html> has given the document room to scroll.
      requestAnimationFrame(() => requestAnimationFrame(reveal));
    } else {
      root.style.removeProperty("--keyboard-h");
    }
  });
  // Moving between fields while the keyboard stays up fires no show event.
  document.addEventListener("focusin", () => {
    if (height > 0) requestAnimationFrame(reveal);
  });
}
